import React, { createContext, useContext, useState } from "react";
import { REQUIRED_FIELDS, EMAIL_REGEX, PHONE_REGEX } from "../utils/constants";

// Form Context for sharing form data across components
const FormContext = createContext();

export const useFormContext = () => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error("useFormContext must be used within a FormProvider");
  }
  return context;
};

export const FormProvider = ({ children }) => {
  const [formData, setFormData] = useState({
    // Section A: Hospital Details
    name: "", // hospitalName -> name
    city: "",
    address: "",
    telephone: "",
    mobile: "",
    fax: "",
    email: "",
    superintendent_name: "", // medicalSuperintendent -> superintendent_name
    superintendent_contact: "", // superintendentPhone -> superintendent_contact
    superintendent_email: "", // superintendentEmail -> superintendent_email
    medical_coordinator: "",
    coordinator_email: "",
    coordinator_contact: "",

    // Section B: NABH Accreditation
    nabh_accredited: "", // nabhAccredited -> nabh_accredited
    nabhCertificateNo: "",
    nabhApplied: "",

    // Section C: Services
    specialties: [{ name: "", head: "" }], // headOfDept -> head

    // Section D: Doctors
    full_time_specialists: "", // fullTimeSpecialists -> full_time_specialists
    duty_doctors: "", // dutyDoctors -> duty_doctors
    resident_medical_officers: "", // residentMedicalOfficers -> resident_medical_officers
    super_specialists: "", // superSpecialists -> super_specialists
    doctors_on_call: "", // doctorsOnCall -> doctors_on_call

    // Section E: Nursing Care
    total_nurses: "", // totalNurses -> total_nurses
    nursing_superintendent: "", // nursingSuperintendent -> nursing_superintendent
    patient_nurse_ratio_general: "", // patientNurseRatioGeneral -> patient_nurse_ratio_general
    patient_nurse_ratio_icu: "", // patientNurseRatioICU -> patient_nurse_ratio_icu

    // Section F: Other Staff
    lab_technicians: "", // labTechnicians -> lab_technicians
    radiographers: "",
    physiotherapists: "",
    dieticians: "",
    admin_staff: "", // administrativeStaff -> admin_staff
    house_keeping: "", // houseKeepingStaff -> house_keeping
    security: "", // securityPersonnel -> security

    // Section G: Infrastructure
    total_beds: "", // totalBeds -> total_beds
    casualty_beds: "", // casualtyBeds -> casualty_beds
    icu_beds: "", // icuBeds -> icu_beds
    ventilators: "",
    general_ward_beds: "", // generalWardBeds -> general_ward_beds
    avg_opd_attendance: "", // averageOPDAttendance -> avg_opd_attendance
    avg_bed_occupancy: "", // averageBedOccupancy -> avg_bed_occupancy
    total_area: "", // totalArea -> total_area
    opd_area: "", // opdArea -> opd_area
    ipd_area: "", // ipdArea -> ipd_area
    wards: "", // numberOfWards -> wards
    ward_dimensions: "", // wardDimensions -> ward_dimensions
    alt_power: "", // alternatePowerSource -> alt_power

    // Section H: Laboratory Services
    biochemistry_head: "", // biochemistryHead -> biochemistry_head
    pathology_head: "", // pathologyHead -> pathology_head
    microbiology_head: "", // microbiologyHead -> microbiology_head

    // Section I: Imaging Facilities
    xray: "", // xRay -> xray
    ultrasonography: "",
    mammography: "",
    ct_scan: "", // ctScan -> ct_scan
    mri_scan: "", // mriScan -> mri_scan

    // Section J: Operation Theatres
    operation_theatres: "", // numberOfOTs -> operation_theatres
    septic_ot: "", // separateOTSeptic -> septic_ot
    ot_24x7: "", // ot24Hours -> ot_24x7

    // Section K: Supportive Services
    blood_bank: "", // bloodBank -> blood_bank
    pharmacy_24hr: "", // pharmacy24Hours -> pharmacy_24hr
    physiotherapy: "",
    cssd: "",
    canteen: "",
    gas_plant: "", // gasPlant -> gas_plant
    medical_records: "", // medicalRecords -> medical_records

    // Section L: Other Tie Ups
    empaneled_cghs: "", // cghsEmpaneled -> empaneled_cghs
    recognized_aarogyasri: "", // stateGovtRecognized -> recognized_aarogyasri
    tpi_tieup: "", // tpiTieUp -> tpi_tieup

    // Section M: Biomedical Waste Management
    biomedical_waste: "", // biomedicalWasteManagement -> biomedical_waste
    pcb_license: "", // pcbLicenseNo -> pcb_license

    // Section N: Bank Account
    bank_name: "", // bankName -> bank_name
    bank_branch: "", // bankBranch -> bank_branch
    account_number: "", // accountNumber -> account_number
    ifsc_code: "", // ifscCode -> ifsc_code
    micr_no: "", // micrNo -> micr_no

    // Document Checklist
    documents: {
      1: { submitted: "", files: [] },
      2: { submitted: "", files: [] },
      3: { submitted: "", files: [] },
      4: { submitted: "", files: [] },
      5: { submitted: "", files: [] },
      6: { submitted: "", files: [] },
      7: { submitted: "", files: [] },
      8: { submitted: "", files: [] },
      9: { submitted: "", files: [] },
      10: { submitted: "", files: [] },
      11: { submitted: "", files: [] },
      12: { submitted: "", files: [] },
      13: { submitted: "", files: [] },
      14: { submitted: "", files: [] },
      15: { submitted: "", files: [] },
      16: { submitted: "", files: [] },
      17: { submitted: "", files: [] },
      18: { submitted: "", files: [] },
      19: { submitted: "", files: [] },
      20: { submitted: "", files: [] },
      21: { submitted: "", files: [] },
      22: { submitted: "", files: [] },
      23: { submitted: "", files: [] },
      24: { submitted: "", files: [] },
      25: { submitted: "", files: [] },
      26: { submitted: "", files: [] },
      27: { submitted: "", files: [] },
      28: { submitted: "", files: [] },
      29: { submitted: "", files: [] },
    },
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState("");

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSpecialtyChange = (index, field, value) => {
    const newSpecialties = [...formData.specialties];
    newSpecialties[index][field] = value;
    setFormData((prev) => ({
      ...prev,
      specialties: newSpecialties,
    }));
  };

  const handleDocumentSubmissionChange = (index, value) => {
    setFormData((prev) => ({
      ...prev,
      documents: {
        ...prev.documents,
        [index]: {
          ...prev.documents[index],
          submitted: value,
          files: value !== "Yes" ? [] : prev.documents[index].files,
        },
      },
    }));
  };

  const handleDocumentFileChange = (index, files) => {
    const fileArray = Array.from(files);
    setFormData((prev) => {
      let existingFiles = prev.documents[index].files || [];

      // Convert old format (plain File objects) to new format (objects with id)
      existingFiles = existingFiles.map((fileOrObj) => {
        if (fileOrObj instanceof File) {
          return {
            id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            file: fileOrObj,
          };
        }
        return fileOrObj;
      });

      // Filter out duplicates by checking name and size
      const newFiles = fileArray.filter((newFile) => {
        return !existingFiles.some(
          (existingFile) =>
            existingFile.file.name === newFile.name &&
            existingFile.file.size === newFile.size &&
            existingFile.file.lastModified === newFile.lastModified
        );
      });

      // Add unique ID to each new file
      const filesWithId = newFiles.map((file) => ({
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        file: file,
      }));

      return {
        ...prev,
        documents: {
          ...prev.documents,
          [index]: {
            ...prev.documents[index],
            files: [...existingFiles, ...filesWithId],
          },
        },
      };
    });
  };

  const removeDocumentFile = (index, fileId) => {
    setFormData((prev) => ({
      ...prev,
      documents: {
        ...prev.documents,
        [index]: {
          ...prev.documents[index],
          files: prev.documents[index].files.filter((fileObj, idx) => {
            // Handle both old format (File) and new format ({id, file})
            if (fileObj instanceof File) {
              // For old format, use index-based removal
              return fileId !== `fallback-${idx}`;
            }
            return fileObj.id !== fileId;
          }),
        },
      },
    }));
  };

  const addSpecialtyRow = () => {
    setFormData((prev) => ({
      ...prev,
      specialties: [...prev.specialties, { name: "", head: "" }],
    }));
  };

  const removeSpecialtyRow = (index) => {
    if (formData.specialties.length > 1) {
      const newSpecialties = formData.specialties.filter((_, i) => i !== index);
      setFormData((prev) => ({
        ...prev,
        specialties: newSpecialties,
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields validation
    REQUIRED_FIELDS.forEach((field) => {
      if (!formData[field]) {
        newErrors[field] = "This field is required";
      }
    });

    // Email validation
    if (formData.email && !EMAIL_REGEX.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (
      formData.superintendentEmail &&
      !EMAIL_REGEX.test(formData.superintendentEmail)
    ) {
      newErrors.superintendentEmail = "Please enter a valid email address";
    }

    // Phone number validation
    if (formData.mobile && !PHONE_REGEX.test(formData.mobile)) {
      newErrors.mobile = "Please enter a valid 10-digit mobile number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setFormData({
      name: "",
      city: "",
      address: "",
      telephone: "",
      mobile: "",
      fax: "",
      email: "",
      superintendent_name: "",
      superintendent_contact: "",
      superintendent_email: "",
      medical_coordinator: "",
      coordinator_email: "",
      coordinator_contact: "",
      nabh_accredited: "",
      nabhCertificateNo: "",
      nabhApplied: "",
      specialties: [{ name: "", head: "" }],
      full_time_specialists: "",
      duty_doctors: "",
      resident_medical_officers: "",
      super_specialists: "",
      doctors_on_call: "",
      total_nurses: "",
      nursing_superintendent: "",
      patient_nurse_ratio_general: "",
      patient_nurse_ratio_icu: "",
      lab_technicians: "",
      radiographers: "",
      physiotherapists: "",
      dieticians: "",
      admin_staff: "",
      house_keeping: "",
      security: "",
      total_beds: "",
      casualty_beds: "",
      icu_beds: "",
      ventilators: "",
      general_ward_beds: "",
      avg_opd_attendance: "",
      avg_bed_occupancy: "",
      total_area: "",
      opd_area: "",
      ipd_area: "",
      wards: "",
      ward_dimensions: "",
      alt_power: "",
      biochemistry_head: "",
      pathology_head: "",
      microbiology_head: "",
      xray: "",
      ultrasonography: "",
      mammography: "",
      ct_scan: "",
      mri_scan: "",
      operation_theatres: "",
      septic_ot: "",
      ot_24x7: "",
      blood_bank: "",
      pharmacy_24hr: "",
      physiotherapy: "",
      cssd: "",
      canteen: "",
      gas_plant: "",
      medical_records: "",
      empaneled_cghs: "",
      recognized_aarogyasri: "",
      tpi_tieup: "",
      biomedical_waste: "",
      pcb_license: "",
      bank_name: "",
      bank_branch: "",
      account_number: "",
      ifsc_code: "",
      micr_no: "",
      documents: {
        1: { submitted: "", files: [] },
        2: { submitted: "", files: [] },
        3: { submitted: "", files: [] },
        4: { submitted: "", files: [] },
        5: { submitted: "", files: [] },
        6: { submitted: "", files: [] },
        7: { submitted: "", files: [] },
        8: { submitted: "", files: [] },
        9: { submitted: "", files: [] },
        10: { submitted: "", files: [] },
        11: { submitted: "", files: [] },
        12: { submitted: "", files: [] },
        13: { submitted: "", files: [] },
        14: { submitted: "", files: [] },
        15: { submitted: "", files: [] },
        16: { submitted: "", files: [] },
        17: { submitted: "", files: [] },
        18: { submitted: "", files: [] },
        19: { submitted: "", files: [] },
        20: { submitted: "", files: [] },
        21: { submitted: "", files: [] },
        22: { submitted: "", files: [] },
        23: { submitted: "", files: [] },
        24: { submitted: "", files: [] },
        25: { submitted: "", files: [] },
        26: { submitted: "", files: [] },
        27: { submitted: "", files: [] },
        28: { submitted: "", files: [] },
        29: { submitted: "", files: [] },
      },
    });
  };

  const value = {
    formData,
    setFormData,
    errors,
    setErrors,
    isSubmitting,
    setIsSubmitting,
    submitStatus,
    setSubmitStatus,
    handleInputChange,
    handleSpecialtyChange,
    addSpecialtyRow,
    removeSpecialtyRow,
    handleDocumentSubmissionChange,
    handleDocumentFileChange,
    removeDocumentFile,
    validateForm,
    resetForm,
  };

  return <FormContext.Provider value={value}>{children}</FormContext.Provider>;
};
